import { useCallback, useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { Toast } from '@highlight/ui'
import { sharedCountdownChatIdsAtom } from '@renderer/overlay/hooks/for-you-linger-state'
import { useForYouLingerControls } from '@renderer/overlay/hooks/for-you-linger'
import { LINGER_CHECK_MS, useLingerCountdownSpent } from '../../../hooks/reminder-linger'
import { useReminderActions } from '../../../hooks/use-reminders'
import { useRemindersData } from '../../../reminders-context'
import { useLatest } from '../../../hooks/use-latest'

const UNDO_TOAST_TRIGGER = 'todos-undo-toast'

/** What To-dos has ticked and is about to clear: resolved reminders still held, and chats on its countdown. */
function useUndoableTicks() {
  const { activeItems } = useRemindersData()
  const chatIds = useAtomValue(sharedCountdownChatIdsAtom)
  // A resolved reminder is only still active while it's held, so these are the ones waiting to go.
  const reminders = activeItems.filter((item) => item.resolved === true)
  return { reminders, chatIds, count: reminders.length + chatIds.size }
}

/**
 * Takes back every tick at once. Stable across renders, and always acts on the ticks as they are when it's
 * called, so a toast opened once can hold it.
 */
function useUndoAllTicks() {
  const { reminders, chatIds } = useUndoableTicks()
  const { setResolved } = useReminderActions()
  const { cancel: cancelLinger } = useForYouLingerControls()
  const undoAll = useLatest(() => {
    for (const { id } of reminders) setResolved(id, false, { trigger: UNDO_TOAST_TRIGGER })
    for (const id of chatIds) cancelLinger(id)
  })
  return useCallback(() => undoAll.current(), [undoAll])
}

/**
 * Raises a toast for what To-dos has ticked, open for as long as the one countdown they all leave on, with an
 * Undo for the lot. Called by the list that runs that countdown, so the toast goes when the list does.
 *
 * The countdown decides when it goes, so its own timer is off. Its message and Undo read the ticks and the
 * countdown themselves, so it opens once rather than being rewritten on every change.
 */
export function useTodosUndoToast() {
  const isCounting = useLingerCountdownSpent() !== null
  const hasTicks = useUndoableTicks().count > 0
  const undoAll = useUndoAllTicks()
  const toast = Toast.useToast()

  const isOpen = isCounting && hasTicks
  useEffect(() => {
    if (!isOpen) return
    const id = toast({
      message: <UndoableCount />,
      action: { label: <UndoLabel />, className: 'relative overflow-hidden', onClick: undoAll },
      timeout: 0,
    })
    return () => toast.close(id)
  }, [isOpen, toast, undoAll])
}

function UndoableCount() {
  const { count } = useUndoableTicks()
  return (
    <>
      {count} {count === 1 ? 'item' : 'items'} marked as complete
    </>
  )
}

/** "Undo", with the time left to take it back sliding out from behind it. */
function UndoLabel() {
  // Null once the countdown ends, while the toast fades out: the time is spent.
  const spent = useLingerCountdownSpent() ?? 1
  return (
    <>
      <span
        aria-hidden="true"
        className="bg-element-active absolute inset-0 origin-left transition-transform ease-linear motion-reduce:transition-none"
        style={{ transform: `scaleX(${1 - spent})`, transitionDuration: `${LINGER_CHECK_MS}ms` }}
      />
      <span className="relative">Undo</span>
    </>
  )
}
